"use client";

import * as React from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import {
    NodeViewContent,
    NodeViewWrapper,
    ReactNodeViewRenderer,
    type ReactNodeViewProps,
} from "@tiptap/react";
import { DEFAULT_BORDER_RADIUS, scaledPx } from "../../lib/utils";
import { BlockSettingsButton, RemoveItemButton } from "../blockControls";
import { BlockItemsReorder } from "../blockItemsReorder";
import { BlockTypeSwitcher } from "../blockTypeSwitcher";
import { SCENE_BLOCK_REGISTRY } from "../registry";
import { useMetadata } from "@/features/lomdaEditorV2/store";
import { Slider } from "@/components/ui/slider";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import ColorPicker from "@/components/ui/color-picker";
import { useLocale } from "@/hooks/useLocale";
import { t } from "@/lib/localization";
import { cn } from "@/lib/utils";
import { RotateCcw } from "lucide-react";

const D = SCENE_BLOCK_REGISTRY.outlineCards;

const CARD_PADDING = 40;

const RADIUS_MIN = 0;
const RADIUS_MAX = 80;
const RADIUS_STEP = 4;
const RADIUS_DEFAULT = DEFAULT_BORDER_RADIUS;

const DEFAULT_BORDER_COLOR = "color-mix(in srgb, currentColor 50%, transparent)";

function getOutlineCardsDefaultItem(language: string) {
    const isHe = language === "he";
    return {
        type: D.itemNodeName,
        content: [
            {
                type: D.titleField.nodeName,
                attrs: { textAlign: "start" },
                content: [
                    {
                        type: "text",
                        text: isHe
                            ? "רעיון מרכזי"
                            : "A key idea",
                    },
                ],
            },
            {
                type: D.bodyField.nodeName,
                attrs: { textAlign: "start" },
                content: [
                    {
                        type: "text",
                        text: isHe
                            ? "משפט קצר שמסביר את הרעיון ומראה מדוע הוא חשוב לנושא"
                            : "A short sentence that explains the idea and why it matters here",
                    },
                ],
            },
        ],
    };
}

export function buildDefaultOutlineCards(
    count: number = D.itemCount.default,
    language: string = "en"
) {
    const item = getOutlineCardsDefaultItem(language);
    return {
        type: D.nodeName,
        content: Array.from({ length: count }, () => item),
    };
}

/* ─── Card style controls (rendered inside the BlockSettingsButton popover) ─── */

/** Small interactive swatch — shows the current color, or a checkered pattern
 *  for transparent. Button is the popover trigger for the color picker. */
function ColorSwatchButton({
    color,
    label,
    onPick,
    onReset,
}: {
    color: string;
    label: string;
    onPick: (color: string) => void;
    onReset?: () => void;
}) {
    const { dir, locale } = useLocale();
    const [open, setOpen] = React.useState(false);
    const isTransparent = !color || color === "transparent";

    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">{label}</span>
            <div className="flex items-center gap-1.5">
                {onReset && !isTransparent ? (
                    <button
                        type="button"
                        aria-label={t("lomda.scene-block.reset", locale)}
                        className="!cursor-pointer text-muted-foreground hover:text-foreground transition-colors inline-flex items-center"
                        onClick={(e) => {
                            e.preventDefault();
                            onReset();
                        }}
                    >
                        <RotateCcw className="size-3" />
                    </button>
                ) : null}
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            aria-label={label}
                            className={cn(
                                "h-7 inline-flex items-center gap-1.5 px-1.5 rounded-md border bg-background hover:bg-muted text-xs !cursor-pointer"
                            )}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <span
                                className="size-4 rounded-sm border shrink-0"
                                style={
                                    isTransparent
                                        ? {
                                              backgroundImage:
                                                  "conic-gradient(#cbd5e1 25%, #fff 0 50%, #cbd5e1 0 75%, #fff 0)",
                                              backgroundSize: "8px 8px",
                                          }
                                        : { backgroundColor: color }
                                }
                            />
                            <span
                                className="text-muted-foreground"
                                dir="ltr"
                            >
                                {isTransparent
                                    ? t(
                                          "lomda.scene-block.transparent",
                                          locale
                                      )
                                    : color}
                            </span>
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        dir={dir}
                        align="end"
                        className="w-auto p-3"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ColorPicker
                            mode="nice-colors"
                            initialColor={isTransparent ? "#000000" : color}
                            onColorChange={(c) => onPick(c)}
                            onPaletteColorSelect={(c) => {
                                onPick(c);
                                setOpen(false);
                            }}
                        />
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
}

interface CardStyleControlsProps {
    radius: number;
    borderColor: string;
    backgroundColor: string;
    onChange: (
        attrs: Partial<{
            cardRadius: number;
            cardBorderColor: string;
            cardBackgroundColor: string;
        }>
    ) => void;
}

function CardStyleControls({
    radius,
    borderColor,
    backgroundColor,
    onChange,
}: CardStyleControlsProps) {
    const { locale } = useLocale();

    return (
        <div className="flex flex-col gap-3">
            {/* Border radius */}
            <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                        {t("lomda.scene-block.border-radius", locale)}
                    </span>
                    <div className="flex items-center gap-1.5">
                        {radius !== RADIUS_DEFAULT ? (
                            <button
                                type="button"
                                aria-label={t(
                                    "lomda.scene-block.reset",
                                    locale
                                )}
                                className="!cursor-pointer text-muted-foreground hover:text-foreground transition-colors inline-flex items-center"
                                onClick={(e) => {
                                    e.preventDefault();
                                    onChange({ cardRadius: RADIUS_DEFAULT });
                                }}
                            >
                                <RotateCcw className="size-3" />
                            </button>
                        ) : null}
                        <span className="text-xs font-medium tabular-nums">
                            {radius}px
                        </span>
                    </div>
                </div>
                <Slider
                    dir="ltr"
                    min={RADIUS_MIN}
                    max={RADIUS_MAX}
                    step={RADIUS_STEP}
                    value={[radius]}
                    onValueChange={([next]) =>
                        onChange({ cardRadius: next })
                    }
                />
            </div>

            {/* Border color */}
            <ColorSwatchButton
                label={t("lomda.scene-block.border-color", locale)}
                color={borderColor}
                onPick={(c) => onChange({ cardBorderColor: c })}
                onReset={
                    borderColor
                        ? () => onChange({ cardBorderColor: "" })
                        : undefined
                }
            />

            {/* Background color */}
            <ColorSwatchButton
                label={t("lomda.scene-block.background-color", locale)}
                color={backgroundColor}
                onPick={(c) => onChange({ cardBackgroundColor: c })}
                onReset={
                    backgroundColor
                        ? () => onChange({ cardBackgroundColor: "" })
                        : undefined
                }
            />
        </div>
    );
}

/* ─── Node views ─── */

function OutlineCardsView({
    node,
    updateAttributes,
    editor,
    getPos,
}: ReactNodeViewProps) {
    const metadata = useMetadata();
    const language = metadata?.language ?? "en";
    // Columns auto-track the item count: each card always takes a full column,
    // so the block fills its parent's full width regardless of card count.
    const count = node.childCount;

    const cardRadius =
        (node.attrs.cardRadius as number | undefined) ?? RADIUS_DEFAULT;
    const cardBorderColor =
        (node.attrs.cardBorderColor as string | undefined) ?? "";
    const cardBackgroundColor =
        (node.attrs.cardBackgroundColor as string | undefined) ?? "";

    const insertCard = () => {
        const pos = typeof getPos === "function" ? getPos() : null;
        if (pos == null) return;
        const end = pos + node.nodeSize - 1;
        editor
            .chain()
            .focus()
            .insertContentAt(end, getOutlineCardsDefaultItem(language))
            .run();
    };

    const deleteBlock = () => {
        const pos = typeof getPos === "function" ? getPos() : null;
        if (pos == null) return;
        editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + node.nodeSize })
            .run();
    };

    const wrapperStyle = {
        "--cols": count,
        "--card-padding": scaledPx(CARD_PADDING),
        "--card-radius": scaledPx(cardRadius),
        "--card-border-color": cardBorderColor || DEFAULT_BORDER_COLOR,
        "--card-bg-color": cardBackgroundColor || "transparent",
    } as React.CSSProperties;

    return (
        <NodeViewWrapper
            as={D.blockTag}
            data-scene-block={D.type}
            className="group/scene-block relative w-full"
            style={wrapperStyle}
        >
            <BlockSettingsButton
                customSection={
                    <div className="flex flex-col gap-3">
                        <BlockTypeSwitcher
                            editor={editor}
                            getPos={getPos}
                            node={node}
                        />
                        <CardStyleControls
                            radius={cardRadius}
                            borderColor={cardBorderColor}
                            backgroundColor={cardBackgroundColor}
                            onChange={updateAttributes}
                        />
                        <BlockItemsReorder
                            editor={editor}
                            getPos={getPos}
                            node={node}
                            getItemMeta={(item) => ({
                                label: item.firstChild?.textContent.trim() ?? "",
                            })}
                        />
                    </div>
                }
                onAddItem={insertCard}
                addItemLabelKey="lomda.scene-block.add-card"
                onDelete={deleteBlock}
            />
            <NodeViewContent as="div" />
        </NodeViewWrapper>
    );
}

function OutlineCardsItemView({ editor, getPos, node }: ReactNodeViewProps) {
    const removeItem = () => {
        const pos = typeof getPos === "function" ? getPos() : null;
        if (pos == null) return;
        editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + node.nodeSize })
            .run();
    };

    return (
        <NodeViewWrapper
            as={D.itemTag}
            className="group/scene-block-item relative"
            // Styles read from CSS vars set on the parent OutlineCards wrapper,
            // so all cards in a block stay visually consistent and the parent's
            // settings popover is the single source of truth.
            // height: 100% so every card matches the tallest in the row —
            // the grid cell stretches, but this inner box doesn't by default.
            style={{
                height: "100%",
                padding: "var(--card-padding)",
                borderRadius: "var(--card-radius)",
                border: "2px solid var(--card-border-color)",
                background: "var(--card-bg-color)",
            }}
        >
            <RemoveItemButton onClick={removeItem} />
            <NodeViewContent as="div" />
        </NodeViewWrapper>
    );
}

/* ─── Tiptap nodes ─── */

const OutlineCards = Node.create({
    name: D.nodeName,
    group: "block",
    content: `${D.itemNodeName}+`,
    defining: true,
    isolating: true,

    addAttributes() {
        return {
            cardRadius: {
                default: RADIUS_DEFAULT,
                parseHTML: (el) => {
                    // Missing attribute → fall back to default. Important:
                    // Number(null) === 0 (which is finite!), so a naive
                    // Number.isFinite check would silently parse "no
                    // attribute" as radius 0 and erase the rounded corners
                    // every time the scene's HTML round-trips.
                    const raw = el.getAttribute("data-card-radius");
                    if (raw == null) return RADIUS_DEFAULT;
                    const v = Number(raw);
                    return Number.isFinite(v) ? v : RADIUS_DEFAULT;
                },
                // Always write the attribute so the round-trip is
                // unambiguous — there is no default to "fall back to" if the
                // attribute is missing, only the explicit value.
                renderHTML: (attrs) => ({
                    "data-card-radius": String(
                        attrs.cardRadius ?? RADIUS_DEFAULT
                    ),
                }),
            },
            cardBorderColor: {
                default: "",
                parseHTML: (el) =>
                    el.getAttribute("data-card-border-color") ?? "",
                renderHTML: (attrs) =>
                    attrs.cardBorderColor
                        ? {
                              "data-card-border-color": String(
                                  attrs.cardBorderColor
                              ),
                          }
                        : {},
            },
            cardBackgroundColor: {
                default: "",
                parseHTML: (el) =>
                    el.getAttribute("data-card-bg-color") ?? "",
                renderHTML: (attrs) =>
                    attrs.cardBackgroundColor
                        ? {
                              "data-card-bg-color": String(
                                  attrs.cardBackgroundColor
                              ),
                          }
                        : {},
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: `${D.blockTag}[data-scene-block=${D.type}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.blockTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block": D.type,
            }),
            0,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(OutlineCardsView);
    },
});

export const OutlineCardsItem = Node.create({
    name: D.itemNodeName,
    group: D.itemNodeName,
    content: `${D.titleField.nodeName} ${D.bodyField.nodeName}`,
    defining: true,
    isolating: true,

    parseHTML() {
        return [
            {
                tag: `${D.itemTag}[data-scene-block-item=${D.type}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.itemTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block-item": D.type,
            }),
            0,
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(OutlineCardsItemView);
    },
});

export const OutlineCardsTitle = Node.create({
    name: D.titleField.nodeName,
    group: `${D.type}Field`,
    content: "inline*",
    defining: true,

    parseHTML() {
        return [
            {
                tag: `${D.titleField.htmlTag}[data-scene-block-field=${D.titleField.nodeName}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.titleField.htmlTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block-field": D.titleField.nodeName,
            }),
            0,
        ];
    },
});

export const OutlineCardsBody = Node.create({
    name: D.bodyField.nodeName,
    group: `${D.type}Field`,
    content: "inline*",
    defining: true,

    parseHTML() {
        return [
            {
                tag: `${D.bodyField.htmlTag}[data-scene-block-field=${D.bodyField.nodeName}]`,
                priority: 100,
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            D.bodyField.htmlTag,
            mergeAttributes(HTMLAttributes, {
                "data-scene-block-field": D.bodyField.nodeName,
            }),
            0,
        ];
    },
});

export const outlineCardsExtensions = [
    OutlineCards,
    OutlineCardsItem,
    OutlineCardsTitle,
    OutlineCardsBody,
];
